import { useCallback } from 'react';
import { alertSrc } from '#shared/assets';
import { OZON_SERVICE_NAME } from '#shared/lib/brand';
import { useConfirm } from '#shared/lib/hooks';
import {
    checkChosenOzonLogisticFbsIntegrationSubType,
    getDisplayTypeFromType,
    INTEGRATION_TYPE_1C,
    INTEGRATION_TYPE_MOYSKLAD,
    INTEGRATION_TYPE_OZON_DELIVERY_FBS,
    isIntegrationSettingsForStocks,
} from '#shared/lib/integrations';
import { showError } from '#shared/lib/toast';
import { type useGetIntegrationSettings } from '../../../hooks/useGetIntegrationSettings';

const CONFIRM_TITLE_BY_SYSTEM_TYPE = {
    [INTEGRATION_TYPE_1C]: `Будут разорваны связи между складами ${getDisplayTypeFromType(INTEGRATION_TYPE_1C)} и ${OZON_SERVICE_NAME} FBS`,
    [INTEGRATION_TYPE_MOYSKLAD]: `Будут разорваны связи между складами ${getDisplayTypeFromType(INTEGRATION_TYPE_MOYSKLAD)} и ${OZON_SERVICE_NAME} FBS`,
} as const;

/**
 * Показывает confirm-диалог при смене/удалении источника данных, если активны связи
 * между складами основной системы (1C или МойСклад) и Ozon FBS в грануле остатков.
 * При подтверждении — выполняет переданные действия, при отмене — прерывает их.
 * @kind helper
 * @param data - настройки интеграции из useGetIntegrationSettings
 */
export const useOzonFbsChangeConfirm = (data: ReturnType<typeof useGetIntegrationSettings>['data']) => {
    const { confirm, renderConfirmModal } = useConfirm();

    const systemType = data?.system_type;
    const isOzonFbsConnectedInStocks =
        isIntegrationSettingsForStocks('stocks', data) && checkChosenOzonLogisticFbsIntegrationSubType(data?.sub_types);

    const needsConfirmation =
        isOzonFbsConnectedInStocks && (systemType === INTEGRATION_TYPE_1C || systemType === INTEGRATION_TYPE_MOYSKLAD);

    const handleConfirmedChange = useCallback(
        async (afterConfirmActions: () => Promise<void>, affectedType: string | undefined) => {
            const isRelevantAffectedType =
                affectedType === INTEGRATION_TYPE_1C ||
                affectedType === INTEGRATION_TYPE_MOYSKLAD ||
                affectedType === INTEGRATION_TYPE_OZON_DELIVERY_FBS;

            if (!needsConfirmation || !isRelevantAffectedType) {
                await afterConfirmActions();

                return;
            }

            // needsConfirmation гарантирует что systemType — это INTEGRATION_TYPE_1C | INTEGRATION_TYPE_MOYSKLAD
            const confirmedSystemType = systemType as keyof typeof CONFIRM_TITLE_BY_SYSTEM_TYPE;

            const isConfirmed = await confirm({
                imageSrc: alertSrc,
                title: CONFIRM_TITLE_BY_SYSTEM_TYPE[confirmedSystemType],
                bodyText: 'При повторном подключении потребуется заново сопоставить склады.',
                applyText: 'Подтвердить',
            });

            if (!isConfirmed) {
                return;
            }

            try {
                await afterConfirmActions();
            } catch (error) {
                showError({ label: 'Произошла ошибка при разрыве связей' }, error);
            }
        },
        [needsConfirmation, systemType, confirm]
    );

    return { handleConfirmedChange, renderConfirmModal };
};
